import { useCallback, useEffect, useRef } from 'react';

import {
  consumeMonitoringTourStartRequest,
  hasMonitoringTourStartRequest,
  subscribeMonitoringTourStartRequest,
} from 'containers/user/critical-elements-monitoring/walkthrough/monitoring-tour-start-request';
import { trackMonitoringWalkthroughStarted } from 'containers/user/critical-elements-monitoring/analytics/monitoring-walkthrough-analytics';
import type { MonitoringWalkthroughEntryPoint } from 'containers/user/critical-elements-monitoring/analytics/monitoring-walkthrough-analytics.constants';
import {
  hasSeenMonitoringTour,
  recordMonitoringTourSeen,
} from 'containers/user/critical-elements-monitoring/walkthrough/monitoring-walkthrough-storage';

export interface UseMonitoringTourStartParams {
  isReady: boolean;
  isAnalyticsEnabled: boolean;
  oktaUserId: string | undefined;
  activeId: string | null;
  start: () => boolean;
}

export const useMonitoringTourStart = ({
  isReady,
  isAnalyticsEnabled,
  oktaUserId,
  activeId,
  start,
}: UseMonitoringTourStartParams): void => {
  const hasAutoStartedRef = useRef(false);

  const attemptStart = useCallback(
    (entryPoint: MonitoringWalkthroughEntryPoint): void => {
      if (activeId !== null || !isReady) return;

      if (entryPoint === 'introPopup' && !consumeMonitoringTourStartRequest()) {
        return;
      }

      const didStart = start();

      if (!didStart) return;

      if (entryPoint === 'firstVisit') {
        hasAutoStartedRef.current = true;
      }

      recordMonitoringTourSeen(oktaUserId);

      if (isAnalyticsEnabled) {
        trackMonitoringWalkthroughStarted(entryPoint);
      }
    },
    [activeId, isReady, isAnalyticsEnabled, oktaUserId, start]
  );

  useEffect(() => {
    if (activeId !== null || !isReady) return;

    if (hasMonitoringTourStartRequest()) {
      attemptStart('introPopup');

      return;
    }

    if (!oktaUserId || hasAutoStartedRef.current) return;

    if (!hasSeenMonitoringTour(oktaUserId)) {
      attemptStart('firstVisit');
    }
  }, [isReady, activeId, oktaUserId, attemptStart]);

  useEffect(
    () =>
      subscribeMonitoringTourStartRequest(() => {
        attemptStart('introPopup');
      }),
    [attemptStart]
  );
};
